import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import api from '../api/client';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

interface ModuleAccessEntry {
  id: string;
  userId: string;
  moduleKey: string;
  hasAccess: boolean;
  isModuleAdmin: boolean;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

interface ModuleAccessResponse {
  access: ModuleAccessEntry[];
  moduleKeys: string[];
}

interface UsersResponse {
  data: User[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

const thStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderTop: '1px solid var(--color-border)',
  fontSize: '0.875rem',
};

const MODULE_LABELS: Record<string, string> = {
  crm: 'CRM',
  'expert-profile': 'Experten Profil',
  infohub: 'Info Hub',
  ctb: 'Compliance Tool Box',
};

export function AdminModulesPage() {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<string | null>(null);

  const { data: usersData, isLoading: usersLoading } = useQuery<UsersResponse>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await api.get<UsersResponse>('/users');
      return res.data;
    },
  });

  const { data: accessData, isLoading: accessLoading } = useQuery<ModuleAccessResponse>({
    queryKey: ['admin', 'module-access'],
    queryFn: async () => {
      const res = await api.get<ModuleAccessResponse>('/rbac/module-access');
      return res.data;
    },
  });

  const toggleAccessMutation = useMutation({
    mutationFn: async ({
      userId,
      moduleKey,
      hasAccess,
      isModuleAdmin,
    }: {
      userId: string;
      moduleKey: string;
      hasAccess: boolean;
      isModuleAdmin: boolean;
    }) => {
      await api.patch(`/rbac/users/${userId}/module-access/${moduleKey}`, {
        hasAccess,
        isModuleAdmin,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'module-access'] });
      queryClient.invalidateQueries({ queryKey: ['rbac', 'my-permissions'] });
      setSaving(null);
    },
    onError: () => {
      setSaving(null);
    },
  });

  const isLoading = usersLoading || accessLoading;
  const users = usersData?.data?.filter((u) => u.isActive) ?? [];
  const allAccess = accessData?.access ?? [];
  const activeModules = (accessData?.moduleKeys ?? []).map((key) => ({
    key,
    name: MODULE_LABELS[key] ?? key,
    isActive: true,
  }));

  const getAccess = (userId: string, moduleKey: string): ModuleAccessEntry | undefined => {
    return allAccess.find((a) => a.userId === userId && a.moduleKey === moduleKey);
  };

  const handleToggleAccess = (userId: string, moduleKey: string, currentAccess: boolean) => {
    const key = `${userId}-${moduleKey}-access`;
    setSaving(key);
    const access = getAccess(userId, moduleKey);
    toggleAccessMutation.mutate({
      userId,
      moduleKey,
      hasAccess: !currentAccess,
      isModuleAdmin: !currentAccess ? (access?.isModuleAdmin ?? false) : false,
    });
  };

  const handleToggleAdmin = (userId: string, moduleKey: string, currentAdmin: boolean) => {
    const key = `${userId}-${moduleKey}-admin`;
    setSaving(key);
    toggleAccessMutation.mutate({
      userId,
      moduleKey,
      hasAccess: true,
      isModuleAdmin: !currentAdmin,
    });
  };

  if (isLoading) return <p>Laden...</p>;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--color-text)' }}>
          Modul-Berechtigungen
        </h2>
        <p style={{ margin: '0.5rem 0 0', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
          Steuern Sie hier, welche Benutzer Zugriff auf welche Module haben.
          Benutzer ohne Zugriff sehen das jeweilige Modul nicht in der Navigation.
          Plattform-Admins haben automatisch Zugriff auf alle Module.
        </p>
      </div>

      {activeModules.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Keine aktiven Module vorhanden.</p>
      ) : (
        <div
          style={{
            background: 'var(--color-bg-card)',
            borderRadius: 8,
            border: '1px solid var(--color-border)',
            overflow: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Benutzer</th>
                <th style={thStyle}>Rolle</th>
                {activeModules.map((mod) => (
                  <th key={mod.key} style={{ ...thStyle, textAlign: 'center' }} colSpan={2}>
                    {MODULE_LABELS[mod.key] ?? mod.name}
                  </th>
                ))}
              </tr>
              <tr>
                <th style={{ ...thStyle, paddingTop: 0 }} />
                <th style={{ ...thStyle, paddingTop: 0 }} />
                {activeModules.map((mod) => (
                  <React.Fragment key={`header-${mod.key}`}>
                    <th
                      style={{
                        ...thStyle,
                        paddingTop: 0,
                        textAlign: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 500,
                      }}
                    >
                      Zugriff
                    </th>
                    <th
                      style={{
                        ...thStyle,
                        paddingTop: 0,
                        textAlign: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 500,
                      }}
                    >
                      Admin
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isPlatformAdmin = user.role === 'PLATFORM_ADMIN';
                return (
                  <tr key={user.id}>
                    <td style={tdStyle}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          background: isPlatformAdmin ? '#dbeafe' : '#f3f4f6',
                          color: isPlatformAdmin ? '#1e40af' : '#374151',
                        }}
                      >
                        {isPlatformAdmin
                          ? 'Plattform-Admin'
                          : user.role === 'TENANT_ADMIN'
                            ? 'Mandanten-Admin'
                            : 'Benutzer'}
                      </span>
                    </td>
                    {activeModules.map((mod) => {
                      const access = getAccess(user.id, mod.key);
                      const hasAccess = isPlatformAdmin || (access?.hasAccess ?? false);
                      const isAdmin = isPlatformAdmin || (access?.isModuleAdmin ?? false);
                      const savingAccess = saving === `${user.id}-${mod.key}-access`;
                      const savingAdmin = saving === `${user.id}-${mod.key}-admin`;

                      return (
                        <React.Fragment key={`${user.id}-${mod.key}`}>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {isPlatformAdmin ? (
                              <span
                                title="Plattform-Admins haben immer Zugriff"
                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                              >
                                <input type="checkbox" checked disabled />
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={hasAccess}
                                onChange={() =>
                                  handleToggleAccess(user.id, mod.key, hasAccess)
                                }
                                disabled={savingAccess}
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                              />
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {isPlatformAdmin ? (
                              <span
                                title="Plattform-Admins sind immer Admin"
                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                              >
                                <input type="checkbox" checked disabled />
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isAdmin}
                                onChange={() =>
                                  handleToggleAdmin(user.id, mod.key, isAdmin)
                                }
                                disabled={!hasAccess || savingAdmin}
                                title={
                                  !hasAccess
                                    ? 'Erst Zugriff aktivieren'
                                    : ''
                                }
                                style={{
                                  cursor: hasAccess ? 'pointer' : 'not-allowed',
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            )}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
